import {IInfo, IInfoConsumer} from "/@lib@/commons/infos.js";
import {EWedletEditMode, IChildWedlet, IParentWedlet, isParentWedlet, IWedlet, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {BarActions} from "/@back@/commons/widgets/bars.js";
import {renderAppend, svg, TemplateResult} from "/@lib@/commons/utils/htmlLit.js";
import {WED} from "/@back@/edit/wed/wedCore.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {DOM, INodeFilter, JSX} from "/@lib@/commons/xml/dom.js";
import {IJmlNode, IJmlSubSet} from "/@lib@/commons/xml/jml.js";
import {GFX, SVG} from "/@lib@/commons/utils/gfx.js";
import {REG} from "/@lib@/commons/registry.js";
import {Action, ACTION, EButtonUiContext, IActionToggle} from "/@lib@/commons/actions.js";
import {ActionActions, OActionActionsInit} from "/@back@/commons/widgets/buttons.js";
import {BoxSpTeRootWedlet, IEltSpTeSource, InfoSpTeFocus, ISpTeForkedWedlet, SPTE, SpTeSegmentWedlet} from "/@back@/edit/wed/wedlets/spte/spte.js";


interface ISpTeSpatialEditor extends IEltSpTeSource, IInfoConsumer {
	tools: IActionToggle<ISpTeSpatialEditor>[];
}

type ISpTeSpatialArea<S extends SVGGraphicsElement = SVGGraphicsElement> = S & { spTeSegment: SpTeSegmentWedlet };

const ZOOM_LEVELS = 3;

interface ISpTeSpatialNode extends GFX.IPoint {
	shape?: 'circle' | 'diamond';
}

/**
 * Widget avec une image de fond et les areas dessinées.
 *
 * Attributs :
 *  - creatable-shapes Liste des formes créables, séparées par des espaces
 */
export class SpTeWallpaper extends HTMLElement implements IEltSpTeSource, IInfoConsumer {

	wedlet: BoxSpTeRootWedlet;

	forkedWedlet: ISpTeForkedWedlet;

	/** modes pour le fork des wedlets fournissant la source et les areas. */
	forkedModes: Dict<string[]>;

	forkedVariants: Dict<string>;

	/** Formes disponibles et listes des outils */
	availableShapes: Dict<ISpTeShape<any>>;
	creatableShapes: string[];
	tools: ISpTeSpatialTool[];
	currentTool: ISpTeSpatialTool;

	get firstAvailableTool() {
		return this.tools.find((tool) => tool.isAvailable(this));
	}

	/** Container et couches graphiques manipulable par les outils */
	scrollBox: HTMLDivElement;
	zoomBox: HTMLDivElement;
	contentBox: HTMLDivElement;
	canvas: SVGSVGElement;
	areasLayer: SVGGElement;
	toolsLayer: SVGGElement;

	/** Mode d'écriture */
	editMode: EWedletEditMode;

	redrawable: boolean;

	protected _toolBar: BarActions<SpTeWallpaper>;
	protected _zoomSelect: HTMLSelectElement;
	protected _currentZoomOption: HTMLOptionElement;
	protected _img: HTMLImageElement;
	protected _insideMask: SVGMaskElement;
	protected _scrollParent: HTMLElement;
	protected _currentToolLstns: ISpTeSpatialListener<ISpTeSpatialTool>[] = [];
	protected _currentZoom: number;
	protected _selectedSegment: SpTeSegmentWedlet;
	protected _redrawViewRequest: number;
	protected _redrawToolsRequest: number;
	protected _resizeObserver: ResizeObserver;
	protected _updateMaskRequest: number;

	protected _zoomToOnRedraw: SVGGraphicsElement;
	protected _updateMaskOnRedraw: boolean;

	protected _percentIntFormat = new Intl.NumberFormat("fr", {style: 'percent', maximumFractionDigits: 0});
	protected _percentOneDigitFormat = new Intl.NumberFormat("fr", {style: 'percent', maximumFractionDigits: 1});

	/** Retourne le segment sélectionné */
	get selectedSegment(): SpTeSegmentWedlet {
		return this._selectedSegment
	}

	/**
	 * Défini la sélection courante
	 * @param segment Le segment a sélectionner
	 * @param internal À définir à true si l'origine de la séléction est interne pour éviter un redraw complet
	 */
	setSelection(segment: SpTeSegmentWedlet, internal = false) {
		if (this._selectedSegment === segment) return;
		this._selectedSegment = segment;
		if (!internal) this._zoomToOnRedraw = this.selectedArea;
		this._toolBar.refreshContent();
		this.redrawSpTeView();
	}

	/** Retourne l'area correspondante à la sélection par parcours des areas disponibles */
	get selectedArea(): ISpTeSpatialArea {
		for (let area = this.areasLayer.firstElementChild as ISpTeSpatialArea; area; area = area.nextElementSibling as ISpTeSpatialArea) {
			if (area.spTeSegment === this._selectedSegment) return area;
		}
		return null;
	}

	/** Lance une requête de redessinement de la vue */
	redrawSpTeView(): void {
		if (!this.redrawable || this._redrawViewRequest) return;
		this._redrawViewRequest = window.requestAnimationFrame(() => {
			this._redrawViewRequest = 0;
			renderAppend(this.tplAreas(), this.areasLayer);
			this.redrawTools();
			if (this._updateMaskOnRedraw) {
				this.updateMask();
				this._updateMaskOnRedraw = false;
			}
		});
	}

	/** Lance une requête de redessinement de l'outil courant */
	redrawTools() {
		if (this._redrawToolsRequest) return;
		this._redrawToolsRequest = window.requestAnimationFrame(() => {
			this._redrawToolsRequest = 0;
			this.clearToolsLayer();
			this.showToolsLayer();
			if (this.redrawable) {
				if (this.currentTool && 'redraw' in this.currentTool) this.currentTool.redraw(this);
				if (this._zoomToOnRedraw) {
					setTimeout(() => {
						if (this._zoomToOnRedraw && this.isSpTeSrcAvailable && !this.isVisible(this._zoomToOnRedraw)) this.zoomToRect(this.canvasContentRect(this._zoomToOnRedraw));
						this._zoomToOnRedraw = null;
					})
				}
			}
		});
	}

	configWedletElt(tpl: Element, wedlet: IWedlet): void {
		this.redrawable = false;
		/* Initialisation du wedlet et des modes */
		this.wedlet = wedlet as BoxSpTeRootWedlet;
		this.wedlet.setSpTeSource(this);
		const modes = WED.parseModes(this.getAttribute("wedlet-modes"));
		if (modes) this.forkedModes = {'': modes};

		/* Récupération des formes disponibles dans le registre et des créables dans l'attribut */
		this.availableShapes = this.wedlet.wedMgr.reg.getListAsMap("wed:spTeSpatial:shapes") as Dict<ISpTeShape<any>>;
		if (this.hasAttribute('creatable-shapes')) this.creatableShapes = this.getAttribute('creatable-shapes').split(' ');
		// Par défaut, on active que les trois formes "historiques"
		else this.creatableShapes = this.wedlet.wedMgr.reg.getPref('spte.creatableShapes', ['rect', 'circle', 'poly']);

		/* Initialisation du shadow */
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.wedlet.wedMgr.reg.installSkin("spte-wallpaper", this.shadowRoot);
		this.wedlet.wedMgr.reg.installSkin("scroll/large", this.shadowRoot);
		this._toolBar = sr.appendChild(new BarActions<SpTeWallpaper>());
		this.scrollBox = sr.appendChild(<div class="scrollBox"/>) as HTMLDivElement;
		this.zoomBox = this.scrollBox.appendChild(<div id="zoomBox"/>) as HTMLDivElement;
		this.contentBox = this.zoomBox.appendChild(<div id="content"/>) as HTMLDivElement;
		this._img = this.contentBox.appendChild(<img/>) as HTMLImageElement;
		this._img.onload = () => {
			if (this.isConnected) this.onSpTeSrcChange();
		};

		this._insideMask = JSX.asSvg(() => <mask id="insideMask">
			<rect x="0" y="0" width="0" height="0" fill="#444"/>
			<rect x="0" y="0" width="0" height="0" fill="white"/>
		</mask>) as SVGMaskElement;
		this.areasLayer = JSX.asSvg(() => <g id="areasLayer" mask="url(#insideMask)"/>) as SVGGElement;
		this.toolsLayer = JSX.asSvg(() => <g id="toolsLayer"/>) as SVGGElement;
		this.canvas = this.contentBox.appendChild(JSX.asSvg(() => <svg focusable="false">{[
			this._insideMask,
			this.areasLayer,
			this.toolsLayer
		]}</svg>)) as SVGSVGElement;

		// Dessin initial de la vue
		this._currentZoom = 1;

		// Gestion de la sélection
		this.addEventListener('focusin', this.onFocusin);
		this.addEventListener('focusout', this.onFocusout);
		this.wedlet.spteInfoBroker.addConsumer(this);

		// Initialisation des outils et de la toolbar
		const toolConstructors = this.wedlet.wedMgr.reg.getList("actions:wed:spte:spatial:tools") as Constructor<ISpTeSpatialTool>[];
		this.tools = [];
		for (const toolConstructor of toolConstructors) {
			const tool = new toolConstructor;
			this.tools.push(tool);
			if ('enable' in tool) tool.enable(this);
		}
		this._toolBar.initialize({
			actions: this.tools,
			actionContext: this,
			groupOrder: "main secondary",
			skinOver: "spte-wallpaper/toolbar"
		});
		/* Initialisation de la liste de choix du zoom */
		this._zoomSelect = this._toolBar.shadowRoot.appendChild(<select id="zoomSelect">
			<option value="toImage">Ajusté</option>
		</select> as HTMLSelectElement);
		this._currentZoomOption = <option value={this.zoomLevel}/> as HTMLOptionElement;
		for (let i = ZOOM_LEVELS; i >= -ZOOM_LEVELS; i--) {
			const level = 1 * Math.pow(2, i);
			this._zoomSelect.appendChild(<option value={level}>{this.formatZoomLevel(level)}</option>);
		}
		this._zoomSelect.addEventListener('change', () => {
			if (this._zoomSelect.value == 'toImage') {
				this.zoomToRect(this.canvasContentRect(this.canvas));
			} else {
				const pivotPoint = {
					x: ((this.scrollBox.clientWidth / 2) + this.scrollBox.scrollLeft - this.contentBox.offsetLeft) / this.zoomLevel,
					y: ((this.scrollBox.clientHeight / 2) + this.scrollBox.scrollTop - this.contentBox.offsetTop) / this.zoomLevel
				};
				this.zoomLevel = parseFloat(this._zoomSelect.value);
				this.centeredOnCanvasPoint(pivotPoint);
			}
		});

		// Désactivation du clic droit sur l'ensemble de la vue
		this.addEventListener('contextmenu', (ev: Event) => {
			if (ev instanceof MouseEvent && ev.shiftKey) return;
			ev.preventDefault();
			ev.stopPropagation();
		});

		this.addEventListener('pointerdown', (ev: MouseEvent) => {
			if (ev.button == 1) ev.preventDefault();
			ev.stopPropagation();
		});

		// Désactivation du drag dans le cas où un ancètre en pose un
		this.setAttribute("draggable", "true");
		this.addEventListener('dragstart', (ev) => {
			ev.preventDefault();
			ev.stopPropagation();
		});
	}

	/** A appeler par le wedlet en cas de changement de IEditableWedlet.editMode. */
	setEditMode(mode: EWedletEditMode): void {
		this.editMode = mode;
		if (this.currentTool) {
			if (this.currentTool.isAvailable(this)) this.activateTool(this.currentTool, true);
			else this.activateTool(this.firstAvailableTool);
		} else {
			this._toolBar.refreshContent();
			this.redrawTools();
		}
	}

	/**
	 * Active un outil, en désactivant au préalable celui courant
	 * @param tool L'outil à activer
	 * @param force Force l'appel à la méthode activate pour même si l'outil n'a pas changé, utile pour les changements de mode d'écriture
	 */
	activateTool(tool: ISpTeSpatialTool, force: boolean = false) {
		if (this.currentTool) {
			if (!force && this.currentTool == tool) return;
			if ('deactivate' in this.currentTool) this.currentTool.deactivate(this);
			// Les listeners propre à l'outil sont systématiquement supprimé sur la désactivation
			this.removeToolListeners();
		}
		this._currentToolLstns = [];
		this.clearToolsLayer();
		this.currentTool = tool;
		if (tool) {
			this.currentTool.activate(this);
			this.redrawTools();
		}
		this._toolBar.refreshContent();
	}

	/**
	 * Ajoute un listener pour l'outil courant
	 * @param lstn Le listener a ajouté, les propriétés tool et ctx sont définie si non précisée
	 * @param lstn Tableau de listener dans lequel ajouter le lstn, pour être passé ensuite à {@link removeToolListeners}
	 */
	addToolListener(lstn: ISpTeSpatialListener<any>, lstns = this._currentToolLstns) {
		lstns.push(lstn);
		if (!lstn.tool) lstn.tool = this.currentTool;
		if (!lstn.ctx) lstn.ctx = this;
		lstn.target.addEventListener(lstn.event, lstn, {capture: lstn.capture, passive: lstn.passive});
	}

	/**
	 * Désenregistre l'ensemble des listeners d'un outil (ou d'une liste passé spécifiquement en paramètre)
	 */
	removeToolListeners(lstns = this._currentToolLstns) {
		for (const lstn of lstns) lstn.target.removeEventListener(lstn.event, lstn, lstn.capture);
	}

	/**
	 * Template de création des areas
	 */
	protected tplAreas(): TemplateResult {
		const areas: TemplateResult[] = [];
		if (this.forkedWedlet) {
			const visitor = (w: IChildWedlet): void | 'stop' => {
				if (w instanceof SpTeSegmentWedlet) {
					const sp = w.getSpatial();
					if (sp) {
						const spShape = sp.getShape();
						const shape = this.availableShapes[spShape];
						if (shape) {
							areas.push(shape.tplArea(spShape, sp.getCoords(), w, this._selectedSegment === w));
						} else {
							console.log(`Unknow shape ${sp.getShape()}`);
						}
					}
				}
				if (isParentWedlet(w)) (w as IParentWedlet).visitWedletChildren(0, Number.MAX_SAFE_INTEGER, visitor);
			};
			this.forkedWedlet.visitWedletChildren(0, Number.MAX_SAFE_INTEGER, visitor);
		}
		return svg`${areas}`;
	}

	refreshBindValue(val: IJmlNode | string, children?: IJmlSubSet): void {
		const wedMgr = this.wedlet.wedMgr;
		if (this.forkedWedlet) {
			this.forkedWedlet.onDelete();
			this.forkedWedlet = null;
		}
		if (val !== null && typeof val === 'object') {
			const model = wedMgr.wedModel.findModelForNode(val, this.forkedModes, this.forkedVariants, this.wedlet);
			if (model) {
				this.forkedWedlet = model.createWedlet(this.wedlet.wedParent) as ISpTeForkedWedlet;
				this.forkedWedlet.spTeViews = [this];
				this.forkedWedlet.bindWithNode(this.wedlet.xaPart, val, children);
			}
		}
	}

	setUrl(url: string) {
		if (url) {
			// this.onSpTeSrcChange() sera appelé sur l'event onload
			this._img.src = url;
		} else {
			this._img.src = '';
			if (this.isConnected) this.onSpTeSrcChange();
		}
	}

	get isSpTeSrcAvailable(): boolean {
		return this._img && this._img.naturalWidth > 0 && this._img.naturalHeight > 0
	};

	get spteSrcDuration(): number | undefined {
		return undefined
	}

	get spteSrcSize(): { w: number, h: number } | undefined {
		return this._img.naturalWidth > 0 && this._img.naturalHeight > 0 ? {w: this._img.naturalWidth, h: this._img.naturalHeight} : undefined
	};

	onInfo(info: IInfo): void {
		if (info instanceof InfoSpTeFocus) {
			//Un autre widget du spTeEditor ou un wedlet Box de l'éditeur principal impose un changement de focus
			if (this.forkedWedlet) {
				this.setSelection(SPTE.findSpTeSegmentWedlet(WEDLET.findWedlet(this.forkedWedlet, info.focusXa, WEDLET.FINDOPTIONS_lastAncestorIfNone)));
			}
		}
	}

	// TODO Voir si la gestion du selectin reste utile
	protected onFocusin(ev: FocusEvent) {
		DOMSH.findHost(this).setAttribute("selectin", "");
		const target = ev.target;
		if (target instanceof SVGGraphicsElement) {
			DOM.setAttrBool(target, "spte-selected", true);
		}
	}

	// TODO Voir si la gestion du selectin reste utile
	protected onFocusout() {
		DOMSH.findHost(this).removeAttribute("selectin");
	}

	connectedCallback() {
		if (this.isSpTeSrcAvailable) this.onSpTeSrcChange();
	}

	/** Appelé sur un changement de l'image source si connecté */
	onSpTeSrcChange() {
		if (!this.isSpTeSrcAvailable) {
			if (this._resizeObserver) {
				this._resizeObserver.disconnect();
				this._resizeObserver = null;
			}
			this.redrawable = false;
			renderAppend(null, this.areasLayer);
			this.activateTool(null);
			this.zoomLevel = 1;
			return;
		}

		this.canvas.setAttribute('viewBox', `0 0 ${this._img.naturalWidth} ${this._img.naturalHeight}`);

		if (!this.redrawable) {
			this._scrollParent = DOMSH.findFlatParentElt(this, document.documentElement, ((node) => {
				return getComputedStyle(node).overflowY != 'visible';
			}) as INodeFilter<HTMLElement>);

			// ResizeObserver
			this._resizeObserver = new ResizeObserver((entries) => {
				if (this._redrawViewRequest) return;
				for (const entry of entries) {
					if (entry.target === this._scrollParent) this.setHeightFromScrollParent();
					else if (entry.target === this) this.updateMask();
				}
			});
			this._resizeObserver.observe(this);
			this._resizeObserver.observe(this._scrollParent);
			this.redrawable = true;
		}
		this.activateTool(this.firstAvailableTool);
		this.setHeightFromScrollParent();
		this._zoomToOnRedraw = this.canvas;
		this._updateMaskOnRedraw = true;
		this.redrawSpTeView();
	}

	get zoomLevel(): number {
		return this._currentZoom;
	}

	set zoomLevel(val: number) {
		this._currentZoom = val;
		if (this.isSpTeSrcAvailable) {
			const {w: imgW, h: imgH} = this.spteSrcSize;
			this._img.width = imgW * this._currentZoom;
			this._img.height = imgH * this._currentZoom;
		}
		this.style.setProperty('--zoom-level', this._currentZoom.toString());
		this.redrawTools();
		this.updateMask();
		this.updateZoomSelect();
	}

	zoomToRect(rect: GFX.IRect): void {
		if (!this.zoomBox.clientWidth || !this.zoomBox.clientHeight) return;
		const {w: imgW, h: imgH} = this.spteSrcSize;
		const targetWidth = imgW + SPATIAL_HANDLERS_WIDTH * 2 + Math.max(-Math.min(rect.x, 0), rect.x + rect.width - imgW) * 2;
		const targetHeight = imgH + SPATIAL_HANDLERS_WIDTH * 2 + Math.max(-Math.min(rect.y, 0), rect.y + rect.height - imgH) * 2;
		this.zoomLevel = Math.min(this.zoomBox.clientWidth / targetWidth, this.zoomBox.clientHeight / targetHeight);
	}

	centeredOnCanvasPoint(point: GFX.IPoint) {
		this.scrollBox.scrollTo(((point.x * this.zoomLevel) - (this.zoomBox.clientWidth / 2)), ((point.y * this.zoomLevel) - (this.zoomBox.clientHeight / 2)));
	}

	clearToolsLayer(): void {
		while (this.toolsLayer.firstChild) this.toolsLayer.removeChild(this.toolsLayer.firstChild);
	}

	hideToolsLayer(): void {
		this.toolsLayer.style.display = 'none';
	}

	showToolsLayer(): void {
		this.toolsLayer.style.display = '';
	}

	setHeightFromScrollParent(): void {
		const scrollHeight = this._scrollParent.getBoundingClientRect().height;
		this.style.maxHeight = (scrollHeight - 40) + 'px';
		this.style.minHeight = scrollHeight / 2 + 'px';
	}

	/** Mise à jour du mask de visibilité */
	updateMask(): void {
		if (this._updateMaskRequest) return;
		this._updateMaskRequest = window.requestAnimationFrame(() => {
			this._updateMaskRequest = 0;
			const zoomRect = this.zoomBox.getBoundingClientRect();
			const canvasRect = this.canvas.getBoundingClientRect();
			const maskBkg = this._insideMask.firstElementChild as SVGRectElement;
			SVG.assignRectLengths(maskBkg, {
				x: (zoomRect.left - canvasRect.left) / this.zoomLevel,
				y: (zoomRect.top - canvasRect.top) / this.zoomLevel,
				width: zoomRect.width / this.zoomLevel,
				height: zoomRect.height / this.zoomLevel
			});
			const maskFrg = this._insideMask.lastElementChild as SVGRectElement;
			if (this.isSpTeSrcAvailable) {
				const {w: imgW, h: imgH} = this.spteSrcSize;
				SVG.assignRectLengths(maskFrg, {
					width: imgW,
					height: imgH
				});
			} else {
				SVG.assignRectLengths(maskFrg, {
					width: 0,
					height: 0
				});
			}
		});
	}

	/** Retourne un rectangle incluant l'élément en paramètre et le canvas */
	canvasContentRect(svgElem: SVGGraphicsElement): GFX.IRect {
		const {w: imgW, h: imgH} = this.spteSrcSize;
		const elemRect = svgElem.getBBox();
		const x = Math.min(elemRect.x, 0);
		const y = Math.min(elemRect.y, 0);
		return {
			x,
			y,
			width: Math.max(elemRect.x + elemRect.width, imgW) - x,
			height: Math.max(elemRect.y + elemRect.height, imgH) - y
		};
	}

	clientToCanvasPoint(clientPoint: GFX.IPoint): GFX.IPoint {
		const canvasBBox = this.canvas.getBoundingClientRect();
		return {
			x: (clientPoint.x - canvasBBox.left) / this.zoomLevel,
			y: (clientPoint.y - canvasBBox.top) / this.zoomLevel
		};
	}

	/** Test si l'élément passé en paramètre est visible */
	isVisible(svgElem: SVGGraphicsElement): boolean {
		const contentRect = this.contentBox.getBoundingClientRect();
		const visibleBox = {
			x: (this.scrollBox.scrollLeft - this.contentBox.offsetLeft) / this.zoomLevel,
			y: (this.scrollBox.scrollTop - this.contentBox.offsetTop) / this.zoomLevel,
			width: this.scrollBox.clientWidth / this.zoomLevel,
			height: this.scrollBox.clientHeight / this.zoomLevel
		};
		const svgElemBBox = svgElem.getBBox();
		if (svgElem instanceof SVGSVGElement) {
			svgElemBBox.x = Math.min(svgElemBBox.x, 0);
			svgElemBBox.y = Math.min(svgElemBBox.y, 0);
			const right = Math.max(svgElemBBox.x + svgElemBBox.width, contentRect.width);
			svgElemBBox.width = right - svgElemBBox.x;
			const bottom = Math.max(svgElemBBox.y + svgElemBBox.height, contentRect.height);
			svgElemBBox.height = bottom - svgElemBBox.y;
		}
		return svgElemBBox.x > visibleBox.x
			&& svgElemBBox.y > visibleBox.y
			&& svgElemBBox.x + svgElemBBox.width < visibleBox.x + visibleBox.width
			&& svgElemBBox.y + svgElemBBox.height < visibleBox.y + visibleBox.height;
	}

	updateZoomSelect(): void {
		this._currentZoomOption.remove();
		this._zoomSelect.selectedIndex = -1;
		let insertCurrentBefore = null;
		for (const option of this._zoomSelect.options) {
			if (option.value != "toImage") {
				const value = parseFloat(option.value);
				if (value == this.zoomLevel) {
					option.selected = true;
					break;
				} else if (value < this.zoomLevel) {
					insertCurrentBefore = option;
					break;
					this._currentZoomOption.textContent = this.formatZoomLevel(this.zoomLevel);
					this._zoomSelect.insertBefore(this._currentZoomOption, option);
					this._currentZoomOption.selected = true;
				}
			}
		}
		if (this._zoomSelect.selectedIndex == -1) {
			this._currentZoomOption.textContent = this.formatZoomLevel(this.zoomLevel);
			this._zoomSelect.insertBefore(this._currentZoomOption, insertCurrentBefore);
			this._currentZoomOption.selected = true;
		}
	}

	formatZoomLevel(level: number) {
		return (level < 1 ? this._percentOneDigitFormat : this._percentIntFormat).format(level);
	}

	refreshToolbarContent() {
		this._toolBar.refreshContent();
	}
}

REG.reg.registerSkin('spte-wallpaper', 1, /* language=CSS */ `
    :host {
        display: flex;
        min-width: 0;
        min-height: 640px;
        flex-direction: column;
        background-color: var(--bgcolor);
    }

	.scrollBox {
		display: flex;
		min-width: 0;
		min-height: 0;
		overflow: auto;
		flex: 1;
		background-color: var(--edit-bgcolor);
		position: relative;
	}

	#zoomBox {
		display: flex;
		min-width: 0;
		min-height: 0;
		flex: 1;
		user-select: none;
	}

	#content {
		position: relative;
		margin: auto;
	}

	#content > img {
		display: block;
	}

	svg {
		display: block;
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		overflow: visible;
	}

    .area {
        stroke-width: 0.5;
        stroke: black;
        stroke-opacity: 1;
        fill: green;
        fill-opacity: 0.3;
    }

	[spte-selected] {
		fill: #2090ff;
	}

    .handler {
        fill: white;
        stroke: black;
        stroke-width: 1;
    }
`);

REG.reg.registerSkin('spte-wallpaper/toolbar', 1, /* language=CSS */ `
	::slotted(hr) {
		border: none;
		flex: 1;
	}

	#zoomSelect {
		border: 1px outset var(--border-color);
		border-radius: 3px;
		margin-inline: 1px;
		font-size: var(--label-size);
		background-color: transparent;
		padding: 2px 4px;
		box-sizing: content-box;
		height: var(--icon-size);
		width: 4.8em;
	}

	#zoomSelect:focus {
		background-color: var(--pressed-bgcolor);
		border-style: inset;
	}
`);

window.customElements.define("spte-wallpaper", SpTeWallpaper);

const SPATIAL_HANDLERS_WIDTH = 10;

/**
 * Interface pour un outil de l'éditeur spatial.
 *
 * L'activation d'un outil désactive tout les autres, c'est pourquoi il hérite de IActionToggle
 */
export interface ISpTeSpatialTool extends IActionToggle<SpTeWallpaper> {

	/**
	 * Appelé dès l'initialisation de l'éditeur.
	 * Permet de définir pour toutes la durées de vie de l'éditeur.
	 * @param ctx
	 */
	enable?(ctx: SpTeWallpaper): void;

	/**
	 * Méthode principale d'éxecution de l'outil/action, à utiliser à la place de l'execute de IAction
	 * @param ctx
	 */
	activate(ctx: SpTeWallpaper): void;

	/**
	 * Méthode de désactivation de l'outil
	 * @param ctx
	 */
	deactivate?(ctx: SpTeWallpaper): void;

	/**
	 * Méthode de dessin de l'outil. Appellé à chaque refresh de l'éditeur
	 * @param ctx
	 */
	redraw?(ctx: SpTeWallpaper): void;
}

/**
 * Interface d'EventListener DOM contenant toutes les propriétés nécessaires à son enregistrement et désenregistrement
 */
interface IEventListenerSelfObject extends EventListenerObject {
	target: EventTarget,
	event: string,
	capture?: boolean,
	passive?: boolean,
	handleEvent: (this: this, evt?: Event) => void;
}

/**
 * Interface de listener contenant une référence à l'outil et le contexte
 */
interface ISpTeSpatialListener<T extends ISpTeSpatialTool> extends IEventListenerSelfObject {
	/** L'outil qui enregistre ce listener, défini par {@link SpTeWallpaper.addToolListener} si non précisé */
	tool?: T,

	/** Le contexte/éditeur, défini par {@link SpTeWallpaper.addToolListener} si non précisé */
	ctx?: SpTeWallpaper,
}

/**
 * Implémentation de base d'un outil
 *
 * Implémente le comportement toggle et redirige l'exécution vers {@link SpTeWallpaper.activateTool}.
 */
abstract class SpTeSpatialTool extends Action<SpTeWallpaper> implements ISpTeSpatialTool {
	isToggle(ctx: SpTeWallpaper): this is IActionToggle<SpTeWallpaper> {
		return true
	}

	getDatas(api: 'toggle', ctx: SpTeWallpaper): boolean | null {
		return ctx.currentTool === this
	}

	isEnabled(ctx: SpTeWallpaper): boolean {
		return ctx.isSpTeSrcAvailable;
	}

	execute(ctx: SpTeWallpaper) {
		ctx.activateTool(this);
	}

	abstract activate(ctx: SpTeWallpaper): void;
}

/**
 * Outil de sélection
 */
class SpTeSpatialToolSelect extends SpTeSpatialTool {
	activate(ctx: SpTeWallpaper) {
		/* Dispatch de l'info de focus sur le focus d'une area */
		/* On ne défini pas le ensureVisible à ce moment là, pour éviter un scroll qui corromprait les actions de drag */
		ctx.addToolListener({
			target: ctx.areasLayer, event: 'focusin', handleEvent: function (ev: UIEvent) {
				const area = ev.target as ISpTeSpatialArea;
				ctx.setSelection(area.spTeSegment, true);
				this.ctx.wedlet.spteInfoBroker.dispatchInfo(new InfoSpTeFocus(area.spTeSegment.wedAnchor), ctx);
			}
		});

		/* Dispatch de l'info de focus avec ensureVisible sur le pointerup */
		ctx.addToolListener({
			target: ctx.areasLayer, event: 'pointerup', handleEvent: function (ev: UIEvent) {
				const area = ev.target as ISpTeSpatialArea;
				this.ctx.wedlet.spteInfoBroker.dispatchInfo(new InfoSpTeFocus(area.spTeSegment.wedAnchor, true), ctx);
			}
		});


		/* Par commoditié, on gère la suppression par la touche Delete dans cet outil */
		if (ctx.editMode === EWedletEditMode.write) ctx.addToolListener({
			target: ctx.zoomBox, event: 'keydown', handleEvent: function (ev: KeyboardEvent) {
				if ((ev.key === 'Delete' || ev.key === 'Backspace') && this.ctx.selectedSegment) {
					this.ctx.wedlet.wedMgr.docHolder.newBatch().deleteSequence(this.ctx.selectedSegment.wedAnchor, 1).doBatch();
				}
			}
		});
	}
}

/**
 * Outil de déplacement de forme
 */
class SpTeSpatialToolMove extends SpTeSpatialTool {
	protected _moveData: {
		area: ISpTeSpatialArea;
		start: GFX.IPoint;
		delta: GFX.IPoint;
		transform: SVGTransform;
		lstns: ISpTeSpatialListener<SpTeSpatialToolMove>[];
	};

	isEnabled(ctx: SpTeWallpaper): boolean {
		return super.isEnabled(ctx) && ctx.editMode === EWedletEditMode.write;
	}

	activate(ctx: SpTeWallpaper) {
		/* Déplacement des formes par drag */
		ctx.addToolListener({
			target: ctx.areasLayer, event: 'pointerdown', handleEvent: function (ev: MouseEvent) {
				if (ev.button != 0) return;
				const area = ev.target as ISpTeSpatialArea;
				/* Une transformation est posée le temps du drag */
				const transform = area.ownerSVGElement.createSVGTransform();
				area.transform.baseVal.initialize(transform);
				this.tool._moveData = {
					area,
					start: {x: ev.screenX, y: ev.screenY},
					delta: {x: 0, y: 0},
					transform,
					lstns: []
				};

				this.ctx.addToolListener({
					target: window, event: 'pointermove', handleEvent: function (ev: MouseEvent) {
						const {start, transform} = this.tool._moveData;
						const delta = {
							x: (ev.screenX - start.x) / this.ctx.zoomLevel,
							y: (ev.screenY - start.y) / this.ctx.zoomLevel
						};
						/* La couche "outils" est caché le temps du drag */
						this.ctx.hideToolsLayer();
						transform.setTranslate(delta.x, delta.y);
						this.tool._moveData.delta.x = delta.x;
						this.tool._moveData.delta.y = delta.y;
					}
				}, this.tool._moveData.lstns);

				this.ctx.addToolListener({
					target: window, event: 'pointerup', capture: true, handleEvent: function () {
						const {area, delta, lstns} = this.tool._moveData;
						this.ctx.removeToolListeners(lstns);
						area.transform.baseVal.removeItem(0);
						if (delta.x || delta.y) {
							const shape = this.ctx.availableShapes[area.getAttribute('spte-shape')];
							// La translation est commité dans la forme
							shape.translateArea(area, delta.x, delta.y);
							this.tool._moveData = null;
							try {
								this.ctx.wedlet.wedMgr.freezeFocus = true;
								area.spTeSegment.getSpatial().updateSpatial(this.ctx.wedlet.wedMgr.docHolder.newBatch(), shape.areaCoords(area)).doBatch();
							} finally {
								this.ctx.wedlet.wedMgr.freezeFocus = false;
							}
						}

					}
				}, this.tool._moveData.lstns);

				const onCancel = () => {
					const {area, lstns} = this.tool._moveData;
					area.transform.baseVal.clear();
					ctx.removeToolListeners(lstns);
					this.tool._moveData = null;
					this.ctx.showToolsLayer();
				}

				ctx.addToolListener({
					target: window, event: 'keydown', capture: true, handleEvent: function (ev: KeyboardEvent) {
						if (ev.key == 'Escape') onCancel();
					}
				}, this.tool._moveData.lstns);
				ctx.addToolListener({target: window, event: 'pointercancel', capture: true, handleEvent: onCancel}, this.tool._moveData.lstns);
			}
		});
	}
}

type ISpTeSpatialHandler = (SVGCircleElement | SVGPolygonElement) & { node: ISpTeSpatialNode };

/** Classe de base pour les outils de manipulation de noeuds */
abstract class SpTeSpatialToolNodesBase extends SpTeSpatialTool {
	isEnabled(ctx: SpTeWallpaper): boolean {
		return super.isEnabled(ctx) && ctx.editMode === EWedletEditMode.write;
	}

	/** Redessine l'ensemble des noeuds de la sélection courante */
	redraw(ctx: SpTeWallpaper) {
		const area = ctx.selectedArea;
		if (!area) return;
		const shape = ctx.availableShapes[area.getAttribute('spte-shape')];
		const nodes = shape.areaNodes(area);
		for (const node of nodes) appendHandler(ctx, node);
	}
}

/**
 * Outil de déplacement de noeuds
 */
class SpTeSpatialToolMoveNodes extends SpTeSpatialToolNodesBase {
	protected _moveData: {
		handler: ISpTeSpatialHandler;
		start: GFX.IPoint;
		nodeStart: GFX.IPoint;
		shape: ISpTeShape<any>;
		lstns: ISpTeSpatialListener<SpTeSpatialToolMoveNodes>[];
	};

	activate(ctx: SpTeWallpaper) {
		ctx.addToolListener({
			target: ctx.toolsLayer, event: 'pointerdown', handleEvent: function (ev: MouseEvent) {
				if (ev.button != 0) return;
				const handler = ev.target as ISpTeSpatialHandler;

				const area = this.ctx.selectedArea;
				const shape = this.ctx.availableShapes[area.getAttribute('spte-shape')];

				this.tool._moveData = {
					handler,
					start: {x: ev.screenX, y: ev.screenY},
					nodeStart: {x: handler.node.x, y: handler.node.y},
					lstns: [],
					shape
				};

				this.ctx.addToolListener({
					target: window, event: 'pointermove', handleEvent: function (ev: MouseEvent) {
						const {handler, start, shape, nodeStart} = this.tool._moveData;
						const delta = {
							x: (ev.screenX - start.x) / this.ctx.zoomLevel,
							y: (ev.screenY - start.y) / this.ctx.zoomLevel
						};

						handler.node.x = nodeStart.x + delta.x;
						handler.node.y = nodeStart.y + delta.y;

						// Dans le cas des polygone, l'affectation des coordonées des points est directe
						if ('updateAreaFromNodes' in shape) {
							const handlers = Array.from(this.ctx.toolsLayer.children) as ISpTeSpatialHandler[];
							shape.updateAreaFromNodes(area, handlers.map((h) => h.node), handlers.indexOf(handler));
						}

						this.ctx.hideToolsLayer();
					}
				}, this.tool._moveData.lstns);

				this.ctx.addToolListener({
					target: window, event: 'pointerup', capture: true, handleEvent: function () {
						const {shape, lstns} = this.tool._moveData;
						this.ctx.removeToolListeners(lstns);

						try {
							this.ctx.wedlet.wedMgr.freezeFocus = true;
							area.spTeSegment.getSpatial().updateSpatial(this.ctx.wedlet.wedMgr.docHolder.newBatch(), shape.areaCoords(area)).doBatch();
						} finally {
							this.ctx.wedlet.wedMgr.freezeFocus = false;
						}
						this.tool._moveData = null;
					}
				}, this.tool._moveData.lstns);

				const onCancel = () => {
					const {handler, nodeStart, lstns} = this.tool._moveData;
					handler.node.x = nodeStart.x;
					handler.node.y = nodeStart.y;
					// Dans le cas des polygone, l'affectation des coordonées des points est directe
					if ('updateAreaFromNodes' in shape) {
						const handlers = Array.from(this.ctx.toolsLayer.children) as ISpTeSpatialHandler[];
						shape.updateAreaFromNodes(area, handlers.map((h) => h.node), handlers.indexOf(handler));
					}
					ctx.removeToolListeners(lstns);
					this.tool._moveData = null;
					this.ctx.showToolsLayer();
				}

				ctx.addToolListener({
					target: window, event: 'keydown', capture: true, handleEvent: function (ev: KeyboardEvent) {
						if (ev.key == 'Escape') onCancel();
					}
				}, this.tool._moveData.lstns);
				ctx.addToolListener({target: window, event: 'pointercancel', capture: true, handleEvent: onCancel}, this.tool._moveData.lstns);
			}
		});
	}

}

/**
 * Outil d'ajout de nœuds
 */
class SpTeSpatialToolAddNodes extends SpTeSpatialToolNodesBase {
	constructor() {
		super('addNodes');
		this._label = "Ajouter des nœuds";
		this._group = "main";
		this._icon = "/@skin@/edit/wed/spte/spatial/addNodes.svg";
	}

	isVisible(ctx: SpTeWallpaper): boolean {
		return ctx.selectedSegment?.getSpatial().getShape() == 'poly';
	}

	protected _data: {
		area: ISpTeSpatialArea<SVGPolygonElement | SVGPolylineElement>;
		polyline: SVGPolylineElement;
		refPointIdx: number;
		lstns: ISpTeSpatialListener<SpTeSpatialToolAddNodes>[];
	};

	activate(ctx: SpTeWallpaper) {
		ctx.addToolListener({
			target: ctx.zoomBox, event: 'pointerdown', handleEvent: function (ev: MouseEvent) {
				const area = this.ctx.selectedArea;
				if (!area || !(area instanceof SVGPolygonElement)) return;

				const canvasPoint = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
				const points = Array.from(area.points as any as Iterable<SVGPoint>);

				/* Recherche du point le plus proche */
				const sortedPoints = Array.from(points).sort((point1, point2) => {
					return GFX.vecLength(canvasPoint, point1) - GFX.vecLength(canvasPoint, point2);
				});
				const closestPointIdx = points.indexOf(sortedPoints[0]);
				/* Recherche des index des points frères les plus proches */
				const prevPointIdx = closestPointIdx > 0 ? closestPointIdx - 1 : points.length - 1;
				const nextPointIdx = closestPointIdx < points.length - 1 ? closestPointIdx + 1 : 0;
				let refPointIdx = -1;
				const closestBrotherPoint = sortedPoints.find((point) => {
					if (point === points[prevPointIdx]) {
						refPointIdx = closestPointIdx;
						return true;
					} else if (point === points[nextPointIdx]) {
						refPointIdx = nextPointIdx;
						return true;
					}
					return false;
				});
				/* Création du polyline de visualisation */
				const polyline = JSX.asSvg(() => <polyline class="area"/>) as SVGPolylineElement;
				polyline.points.appendItem(sortedPoints[0]);
				polyline.points.appendItem(this.ctx.canvas.createSVGPoint());
				polyline.points.appendItem(closestBrotherPoint);
				this.ctx.toolsLayer.insertBefore(polyline, this.ctx.toolsLayer.firstChild);
				Object.assign(polyline.points.getItem(1), canvasPoint);

				this.tool._data = {
					polyline,
					lstns: [],
					area,
					refPointIdx
				};

				this.ctx.addToolListener({
					target: window, event: 'pointermove', handleEvent: function (ev: MouseEvent) {
						const {polyline} = this.tool._data;
						const canvasPoint = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
						Object.assign(polyline.points.getItem(1), canvasPoint);
					}
				}, this.tool._data.lstns);

				this.ctx.addToolListener({
					target: window, event: 'pointerup', handleEvent: function (ev: MouseEvent) {
						if (ev.button != 0) return;
						const {area, polyline, refPointIdx, lstns} = this.tool._data;
						if (polyline) {
							const shape = this.ctx.availableShapes[area.getAttribute('spte-shape')];
							// Insertion du nouveau point dans le polygone
							area.points.insertItemBefore(polyline.points.getItem(1), refPointIdx);
							polyline.remove();
							try {
								this.ctx.wedlet.wedMgr.freezeFocus = true;
								area.spTeSegment.getSpatial().updateSpatial(this.ctx.wedlet.wedMgr.docHolder.newBatch(), shape.areaCoords(area)).doBatch();
							} finally {
								this.ctx.wedlet.wedMgr.freezeFocus = false;
							}
							ctx.refreshToolbarContent();
						}
						this.ctx.removeToolListeners(lstns);
					}
				}, this.tool._data.lstns);
			}
		});


	}
}

/**
 * Outil de suppression de nœuds
 */
class SpTeSpatialToolRemoveNodes extends SpTeSpatialToolNodesBase {
	constructor() {
		super('removeNodes');
		this._label = "Supprimer des nœuds";
		this._group = "main";
		this._icon = "/@skin@/edit/wed/spte/spatial/rmNodes.svg";
	}

	isVisible(ctx: SpTeWallpaper): boolean {
		return ctx.selectedSegment?.getSpatial().getShape() == 'poly';
	}

	isEnabled(ctx: SpTeWallpaper): boolean {
		return ctx.selectedArea instanceof SVGPolygonElement && ctx.selectedArea.points.numberOfItems > 3;
	}

	activate(ctx: SpTeWallpaper) {
		ctx.addToolListener({
			target: ctx.toolsLayer, event: 'pointerup', handleEvent: function (ev: MouseEvent) {
				if (ev.button != 0 || !(ev.target instanceof SVGCircleElement)) return;
				const handler = ev.target as ISpTeSpatialHandler;
				const area = this.ctx.selectedArea;
				if (area instanceof SVGPolygonElement && area.points.numberOfItems > 3) {
					area.points.removeItem(Array.prototype.findIndex.call(area.points, (point: SVGPoint) => {
						/* TODO On passe par une comparaison des coordonnées car getItem retourne dans certains cas (lesquelles?) une copie du point */
						return point.x === handler.node.x && point.y === handler.node.y
					}));
					const shape = this.ctx.availableShapes[area.getAttribute('spte-shape')];
					try {
						this.ctx.wedlet.wedMgr.freezeFocus = true;
						area.spTeSegment.getSpatial().updateSpatial(this.ctx.wedlet.wedMgr.docHolder.newBatch(), shape.areaCoords(area)).doBatch();
					} finally {
						this.ctx.wedlet.wedMgr.freezeFocus = false;
					}
					ctx.refreshToolbarContent();
					ev.stopPropagation();
				}
			}
		});
	}
}

/**
 * Outil de manipulation principale, regroupant la sélection et le déplacement de formes et de nœuds
 */
class SpTeSpatialToolHandle extends SpTeSpatialTool {
	protected _subTools: ISpTeSpatialTool[];

	constructor() {
		super('handle');
		this._label = "Modification";
		this._group = "main";
		this._icon = "/@skin@/edit/wed/spte/spatial/handle.svg";
		this._subTools = [new SpTeSpatialToolSelect(), new SpTeSpatialToolMove(), new SpTeSpatialToolMoveNodes()];
	}

	activate(ctx: SpTeWallpaper) {
		for (const subTool of this._subTools) if ('activate' in subTool && subTool.isAvailable(ctx)) subTool.activate(ctx);
	}

	deactivate(ctx: SpTeWallpaper) {
		for (const subTool of this._subTools) if ('deactivate' in subTool && subTool.isAvailable(ctx)) subTool.deactivate(ctx);
	}

	redraw(ctx: SpTeWallpaper) {
		for (const subTool of this._subTools) if ('redraw' in subTool && subTool.isAvailable(ctx)) subTool.redraw(ctx);
	}
}

/**
 * Outil de zoom et de pan
 * @todo le pan
 */
class SpTeSpatialToolZoomAndPan extends SpTeSpatialTool {
	protected _zoomOutModifier: boolean;

	constructor() {
		super('zoom');
		this._label = "Zoom";
		this._group = "secondary";
		this._icon = "/@skin@/edit/wed/spte/spatial/zoom.svg";
		this._zoomOutModifier = false;
	}

	enable(ctx: SpTeWallpaper) {
		// Ajout d'un listener pour effectuer un zoom sur le Ctrl+Molette au lieu du zoom de page
		// Le preventDefault implique que le listener ne soit pas 'passive' (avertissement dans Chrome)
		ctx.zoomBox.addEventListener('wheel', (ev: WheelEvent) => {
			if (!ACTION.isAccelPressed(ev)) return;
			if (ctx.redrawable) {
				const pivotPoint = ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
				const zoomed = ev.deltaY < 0 ? this._zoomIn(ctx) : this._zoomOut(ctx);
				if (zoomed) ctx.centeredOnCanvasPoint(pivotPoint);
			}
			ev.preventDefault();
		});
	}

	activate(ctx: SpTeWallpaper) {
		ctx.zoomBox.style.cursor = 'zoom-in';

		ctx.addToolListener({
			target: ctx.zoomBox, event: 'pointerup', handleEvent: function (ev: MouseEvent) {
				if (ev.button !== 0) return;
				ev.preventDefault();
				ev.stopPropagation();
				const pivotPoint = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
				const zoomed = this.tool._zoomOutModifier ? this.tool._zoomOut(this.ctx) : this.tool._zoomIn(this.ctx);
				if (zoomed) ctx.centeredOnCanvasPoint(pivotPoint);
			}
		});

		ctx.addToolListener({
			target: window, event: 'keydown', handleEvent: function (ev: KeyboardEvent) {
				if (ev.key === 'Control') {
					this.tool._zoomOutModifier = true;
					this.ctx.zoomBox.style.cursor = 'zoom-out';
				} else if (ev.key == 'Escape') {
					ctx.activateTool(ctx.firstAvailableTool);
				}
			}
		});

		ctx.addToolListener({
			target: window, event: 'keyup', handleEvent: function (ev: KeyboardEvent) {
				if (ev.key === 'Control') {
					this.tool._zoomOutModifier = false;
					this.ctx.zoomBox.style.cursor = 'zoom-in';
				}
			}
		});

		ctx.addToolListener({
			target: window, event: 'focusout', handleEvent: function (ev: KeyboardEvent) {
				this.tool._zoomOutModifier = false;
				this.ctx.zoomBox.style.cursor = 'zoom-in';
			}
		});

		ctx.addToolListener({
			target: ctx.zoomBox, event: 'contextmenu', handleEvent: function (ev: MouseEvent) {
				const pivotPoint = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
				const zoomed = this.tool._zoomOut(this.ctx);
				if (zoomed) ctx.centeredOnCanvasPoint(pivotPoint);
				this.ctx.zoomBox.style.cursor = 'zoom-out';
				clearTimeout(this.tool._zoomOutCursorTimeout);
				this.tool._zoomOutCursorTimeout = setTimeout(() => {
					this.ctx.zoomBox.style.cursor = 'zoom-in';
				}, 300);
			}
		});
	}

	protected _zoomIn(wallpaper: SpTeWallpaper): boolean {
		const maxLevel = Math.pow(2, ZOOM_LEVELS);
		if (wallpaper.zoomLevel >= maxLevel) return false;
		wallpaper.zoomLevel = Math.min(maxLevel, wallpaper.zoomLevel * Math.sqrt(2));
		return true;
	}

	protected _zoomOut(wallpaper: SpTeWallpaper): boolean {
		const minLevel = Math.pow(2, -ZOOM_LEVELS);
		if (wallpaper.zoomLevel <= minLevel) return false;
		wallpaper.zoomLevel = Math.max(minLevel, wallpaper.zoomLevel / Math.sqrt(2));
		return true;
	}

	deactivate(ctx: SpTeWallpaper): void {
		ctx.zoomBox.style.cursor = '';
	}

}

/**
 * Outil de création de forme
 */
class SpTeSpatialToolCreate extends SpTeSpatialTool {
	currentCreateAction: SpTeSpatialCreateAction;

	protected _fromRectData: {
		area: SVGGraphicsElement;
		start: GFX.IPoint;
		current?: GFX.IPoint;
		lstns: ISpTeSpatialListener<ISpTeSpatialTool>[];
	};

	protected _fromPolyData: {
		polyline?: SVGPolylineElement;
		lastPoint?: SVGPoint;
		startHandler?: ISpTeSpatialHandler;
		lstns: ISpTeSpatialListener<ISpTeSpatialTool>[];
	};

	constructor() {
		super('create');
		this._label = "Création";
		this._group = "main";
	}

	buildCustomButton(ctx: SpTeWallpaper, uiContext: EButtonUiContext, parent?: Element): Element | null {
		if (!this.isVisible(ctx)) return null;
		const shapes = ctx.creatableShapes || Object.keys(ctx.availableShapes);
		if (shapes.length == 1) return null;
		const actions = shapes.map((shapeKey) => ctx.availableShapes[shapeKey].createAction);
		this.currentCreateAction = actions[0];
		return new ActionActions<ICreateActionContext>().initialize({
			reg: REG.findReg(ctx),
			actionContext: {
				createTool: this,
				wallpaper: ctx
			},
			actionBtn: {
				action: actions[0]
			},
			actionsBtn: {
				actions
			},
			uiContext: uiContext,
			lastAsDefault: true
		} as OActionActionsInit<ICreateActionContext>);
	}

	activate(ctx: SpTeWallpaper): void {
		const baseOnCancel = () => {
			ctx.selectedArea.style.display = '';
			ctx.activateTool(ctx.firstAvailableTool);
		}
		let onCancel = baseOnCancel;

		ctx.addToolListener({
			target: ctx.zoomBox, event: 'pointerdown', handleEvent: function (ev: MouseEvent) {
				const shape = this.tool.currentCreateAction.shape;
				if ('updateAreaFromRect' in shape) {
					if (ev.button != 0) return;

					ctx.selectedArea.style.display = 'none';

					onCancel = () => {
						const {lstns} = this.tool._fromRectData;
						this.ctx.removeToolListeners(lstns);
						this.tool._fromRectData = null;
						baseOnCancel();
					}

					const area = this.ctx.toolsLayer.appendChild(shape.emptyArea());

					this.tool._fromRectData = {
						area,
						start: this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY}),
						lstns: []
					};

					this.ctx.addToolListener({
						target: window, event: 'pointermove', handleEvent: function (ev: MouseEvent) {
							const {area, start} = this.tool._fromRectData;
							const current = this.tool._fromRectData.current = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
							shape.updateAreaFromRect(area, start, current);
						}
					}, this.tool._fromRectData.lstns);

					this.ctx.addToolListener({
						target: window, event: 'pointerup', capture: true, handleEvent: function () {
							const {area, lstns, current} = this.tool._fromRectData;
							this.ctx.removeToolListeners(lstns);
							onCancel = baseOnCancel;
							this.tool._fromRectData = null;
							if (!current) return;
							try {
								this.ctx.wedlet.wedMgr.freezeFocus = true;
								const selectedSegment = ctx.selectedSegment;
								selectedSegment.getSpatial().updateSpatial(this.ctx.wedlet.wedMgr.docHolder.newBatch(), shape.areaCoords(area), shape.shape).doBatch();
							} finally {
								this.ctx.wedlet.wedMgr.freezeFocus = false;
							}
							this.ctx.selectedArea.style.display = '';
							this.ctx.activateTool(this.ctx.firstAvailableTool);
						}
					}, this.tool._fromRectData.lstns);
				} else {
					ctx.selectedArea.style.display = 'none';

					onCancel = () => {
						const {lstns} = this.tool._fromPolyData;
						this.ctx.removeToolListeners(lstns);
						this.tool._fromPolyData = null;
						baseOnCancel();
					}

					if (ev.button == 0) {
						// Ajout d'un nouveau point sur le clic gauche
						const canvasPoint = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
						if (!this.tool._fromPolyData) {
							const startSvgPoint = this.ctx.canvas.createSVGPoint();
							startSvgPoint.x = canvasPoint.x;
							startSvgPoint.y = canvasPoint.y;

							this.tool._fromPolyData = {
								polyline: this.ctx.toolsLayer.appendChild(JSX.asSvg(() => <polyline class="area"/>) as SVGPolylineElement),
								lastPoint: null,
								startHandler: appendHandler(ctx, canvasPoint),
								lstns: []
							};
							this.tool._fromPolyData.polyline.points.appendItem(startSvgPoint);
						}
						const lastPoint = this.tool._fromPolyData.lastPoint = this.ctx.canvas.createSVGPoint();
						lastPoint.x = canvasPoint.x;
						lastPoint.y = canvasPoint.y;
						this.tool._fromPolyData.polyline.points.appendItem(lastPoint);

						this.tool._addLastPointListeners(ctx, onCancel);
					} else if (ev.button == 2 && this.tool._fromPolyData) {
						// Suppression du dernier point sur le clic droit
						const {polyline, lstns} = this.tool._fromPolyData;
						if (polyline.points.numberOfItems > 2) {
							const canvasPoint = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
							polyline.points.removeItem(polyline.points.numberOfItems - 1);
							const lastPoint = this.tool._fromPolyData.lastPoint = polyline.points.getItem(polyline.points.numberOfItems - 1);
							lastPoint.x = canvasPoint.x;
							lastPoint.y = canvasPoint.y;
							this.tool._addLastPointListeners(ctx, onCancel);
						}
					}

					ctx.addToolListener({
						target: window, event: 'keydown', capture: true, handleEvent: function (ev: KeyboardEvent) {
							if (ev.key == 'Escape') onCancel();
						}
					}, this.tool._fromPolyData.lstns);
					ctx.addToolListener({target: window, event: 'pointercancel', capture: true, handleEvent: onCancel}, this.tool._fromPolyData.lstns);
				}
			}
		});

		ctx.addToolListener({
			target: window, event: 'keydown', capture: true, handleEvent: function (ev: KeyboardEvent) {
				if (ev.key == 'Escape') onCancel();
			}
		});
		ctx.addToolListener({target: window, event: 'pointercancel', capture: true, handleEvent: onCancel});

	}

	_addLastPointListeners(ctx: SpTeWallpaper, onCancel: () => void) {
		ctx.removeToolListeners(this._fromPolyData.lstns);
		this._fromPolyData.lstns = [];
		const shape = this.currentCreateAction.shape;

		ctx.addToolListener({
			target: window, event: 'pointermove', handleEvent: function (ev: MouseEvent) {
				const {lastPoint} = this.tool._fromPolyData;
				const canvasPoint = this.ctx.clientToCanvasPoint({x: ev.clientX, y: ev.clientY});
				lastPoint.x = canvasPoint.x;
				lastPoint.y = canvasPoint.y;
			}
		}, this._fromPolyData.lstns);

		ctx.addToolListener({
			target: ctx.toolsLayer, event: 'pointerdown', handleEvent: function (ev: MouseEvent) {
				const startHandler = this.tool._fromPolyData.startHandler;
				if (!startHandler || ev.target !== startHandler) return;
				ev.stopPropagation();
				const {polyline, lstns} = this.tool._fromPolyData;
				// Annulation si moins de 3 points
				if (polyline.points.numberOfItems <= 3) return onCancel();

				this.ctx.removeToolListeners(lstns);
				this.tool._fromPolyData = null;
				// Suppression du lastPoint sur la fermeture du polygone
				polyline.points.removeItem(polyline.points.numberOfItems - 1);
				try {
					this.ctx.wedlet.wedMgr.freezeFocus = true;
					const selectedSegment = this.ctx.selectedSegment;
					selectedSegment.getSpatial().updateSpatial(this.ctx.wedlet.wedMgr.docHolder.newBatch(), shape.areaCoords(polyline), shape.shape).doBatch();
				} finally {
					this.ctx.wedlet.wedMgr.freezeFocus = false;
				}
				this.ctx.selectedArea.style.display = '';
				this.ctx.activateTool(this.ctx.firstAvailableTool);
			}
		}, this._fromPolyData.lstns);
	}

	deactivate(ctx: SpTeWallpaper) {
		if (ctx.selectedArea) ctx.selectedArea.style.display = '';
	}
}

type ICreateActionContext = {
	createTool: SpTeSpatialToolCreate,
	wallpaper: SpTeWallpaper
}

/**
 * Classe de base pour les actions de création de forme
 */
class SpTeSpatialCreateAction extends Action<ICreateActionContext> implements IActionToggle<ICreateActionContext> {
	isToggle() {
		return true;
	}

	getDatas(api: 'toggle', ctx: ICreateActionContext): boolean | null {
		return ctx.wallpaper.currentTool === ctx.createTool && ctx.createTool.currentCreateAction === this
	};

	shape: ISpTeShape<SVGGraphicsElement>;

	isVisible(ctx: ICreateActionContext): boolean {
		return (!ctx.wallpaper.creatableShapes && Object.keys(ctx.wallpaper.availableShapes).length > 1) || ctx.wallpaper.creatableShapes.length > 1;
	}

	isEnabled(ctx: ICreateActionContext): boolean {
		return super.isEnabled(ctx) && ctx.wallpaper.selectedSegment && ctx.wallpaper.editMode === EWedletEditMode.write;
	}

	constructor(shape: ISpTeShape<any>) {
		super('create-' + shape.shape);
		this.shape = shape;
	}

	execute(ctx: ICreateActionContext, ev: Event) {
		ctx.createTool.currentCreateAction = this;
		ctx.wallpaper.activateTool(ctx.createTool);
		return this;
	}
}

REG.reg.addToList("actions:wed:spte:spatial:tools", "handler", 1, SpTeSpatialToolHandle, 10);
REG.reg.addToList("actions:wed:spte:spatial:tools", "create", 1, SpTeSpatialToolCreate, 20);
REG.reg.addToList("actions:wed:spte:spatial:tools", "addNodes", 1, SpTeSpatialToolAddNodes, 30);
REG.reg.addToList("actions:wed:spte:spatial:tools", "removeNodes", 1, SpTeSpatialToolRemoveNodes, 40);
REG.reg.addToList("actions:wed:spte:spatial:tools", "zoom", 1, SpTeSpatialToolZoomAndPan, 100);

/**
 * Interface pour l'ajout d'une forme
 */
interface ISpTeShape<S extends SVGGraphicsElement> {
	/**
	 * Nom de la forme tel que stocké dans le flux XML
	 */
	shape: string;

	/**
	 * Template lit de création de la forme
	 */
	tplArea(spShape: string, spCoords: string, spTeSegment: SpTeSegmentWedlet, selected: boolean): TemplateResult;

	/**
	 * Retourne une forme vide pour l'outil de création
	 */
	emptyArea(): S;

	/**
	 * Translate la forme
	 */
	translateArea(area: S, x: number, y: number): void;

	/**
	 * Retourne les coordonnées à stocker dans le flux XML
	 */
	areaCoords(area: S): string;

	/**
	 * Retourne la liste des points manipulables par les outils de nœuds
	 */
	areaNodes(area: S): Iterable<GFX.IPoint>;

	/**
	 * Mets à jour la forme par une nouvelle liste de nœuds
	 * Peut ne pas être définie si {@link areaNodes} retourne des SVGPoint directement modifiable (ex: polygone)
	 */
	updateAreaFromNodes?(area: S, nodes: ISpTeSpatialNode[], nodeIndex: number): void;

	/**
	 * Mets à jour la forme en fonction d'un carré défini par deux points
	 * Utile pour l'outil de création par drag (ex: rect, circle)
	 */
	updateAreaFromRect?(area: S, point1: GFX.IPoint, point2: GFX.IPoint): void;

	/**
	 * Définition de l'action de création pour cette forme
	 */
	createAction: SpTeSpatialCreateAction;
}

class SpTeShapeRect implements ISpTeShape<SVGRectElement> {
	shape: 'rect' | 'roundedRect' = null;

	createAction: SpTeSpatialCreateAction = null;

	constructor(readonly rounded = false) {
		if (rounded) {
			this.shape = 'roundedRect';
			this.createAction = new SpTeSpatialCreateAction(this)
				.setLabel("Rectangle arrrondi")
				.setDescription("Création d'un rectangle arrrondi")
				.setIcon("/@skin@/edit/wed/spte/spatial/roundedRect.svg");
		} else {
			this.shape = 'rect';
			this.createAction = new SpTeSpatialCreateAction(this)
				.setLabel("Rectangle")
				.setDescription("Création d'un rectangle")
				.setIcon("/@skin@/edit/wed/spte/spatial/rect.svg");
		}
	}

	tplArea(spShape: string, spCoords: string, spTeSegment: SpTeSegmentWedlet, selected: boolean): TemplateResult {
		const coords = spCoords ? spCoords.split(',').map((coord) => parseFloat(coord)) : null;
		return svg`<rect class="area" x="${coords?.[0] || '0'}" y="${coords?.[1] || '0'}" width="${(coords?.[2] || 0) - (coords?.[0] || 0)}" height="${(coords?.[3] || 0) - (coords?.[1] || 0)}" rx="${(coords?.[4] || 0)}" p.spTeSegment="${spTeSegment}" b.spte-selected="${selected}" spte-shape="${spShape}" tabindex="0"/>`;
	}

	emptyArea(): SVGRectElement {
		return JSX.asSvg(() => <rect class="area"/>) as SVGRectElement;
	}

	translateArea(area: SVGRectElement, x: number, y: number): void {
		area.x.baseVal.value += x;
		area.y.baseVal.value += y;
	}

	areaNodes(area: SVGRectElement): Iterable<GFX.IPoint> {
		const nodes = [
			{x: area.x.baseVal.value, y: area.y.baseVal.value},
			{x: area.x.baseVal.value + area.width.baseVal.value, y: area.y.baseVal.value + area.height.baseVal.value},
		];
		if (this.rounded) nodes.push(new RoundedRectNode(area, area.y.baseVal.value + area.rx.baseVal.value));
		return nodes;
	}

	updateAreaFromRect(area: SVGRectElement, point1: GFX.IPoint, point2: GFX.IPoint) {
		SVG.assignRectLengths(area, {
			x: Math.min(point1.x, point2.x),
			y: Math.min(point1.y, point2.y),
			width: point2.x > point1.x ? point2.x - point1.x : point1.x - point2.x,
			height: point2.y > point1.y ? point2.y - point1.y : point1.y - point2.y
		});
	}

	updateAreaFromNodes(area: SVGRectElement, nodes: ISpTeSpatialNode[], nodeIndex: number): void {
		if (nodeIndex < 2) {
			const x = Math.min(nodes[0].x, nodes[1].x);
			const y = Math.min(nodes[0].y, nodes[1].y);
			const width = Math.max(nodes[0].x, nodes[1].x) - x;
			const height = Math.max(nodes[0].y, nodes[1].y) - y;
			SVG.assignRectLengths(area, {x, y, width, height});
		} else if (this.rounded) {
			area.rx.baseVal.value = nodes[2].y - area.y.baseVal.value;
		}
	}

	areaCoords(area: SVGRectElement): string {
		const coords = [
			area.x.baseVal.value,
			area.y.baseVal.value,
			area.x.baseVal.value + area.width.baseVal.value,
			area.y.baseVal.value + area.height.baseVal.value
		];
		if (this.rounded) coords.push(area.rx.baseVal.value);
		return coords.join(',');
	}
}


class SpTeShapeCircle implements ISpTeShape<SVGCircleElement> {
	readonly shape = 'circle';

	createAction: SpTeSpatialCreateAction = new SpTeSpatialCreateAction(this)
		.setLabel("Cercle")
		.setDescription("Création d'un cercle")
		.setIcon("/@skin@/edit/wed/spte/spatial/circle.svg");

	tplArea(spShape: string, spCoords: string, spTeSegment: SpTeSegmentWedlet, selected: boolean): TemplateResult {
		const coords = spCoords.split(',');
		return svg`<circle class="area" cx="${coords?.[0] || '0'}" cy="${coords?.[1] || '0'}" r="${coords?.[2] || '0'}" p.spTeSegment="${spTeSegment}" b.spte-selected="${selected}" spte-shape="${spShape}" tabindex="0"/>`;
	}

	emptyArea(): SVGCircleElement {
		return JSX.asSvg(() => <circle class="area"/>) as SVGCircleElement;
	}

	translateArea(area: SVGCircleElement, x: number, y: number): void {
		area.cx.baseVal.value += x;
		area.cy.baseVal.value += y;
	}

	updateAreaFromNodes(area: SVGCircleElement, nodes: ISpTeSpatialNode[], nodeIndex: number): void {
		area.r.baseVal.value = Math.abs(nodes[0].x - area.cx.baseVal.value);
	}

	updateAreaFromRect(area: SVGCircleElement, point1: GFX.IPoint, point2: GFX.IPoint) {
		area.cx.baseVal.value = point1.x;
		area.cy.baseVal.value = point1.y;
		area.r.baseVal.value = Math.abs(point2.x - point1.x);
	}

	areaNodes(area: SVGCircleElement): Iterable<GFX.IPoint> {
		return [
			{x: area.cx.baseVal.value + area.r.baseVal.value, y: area.cy.baseVal.value}
		];
	}

	areaCoords(area: SVGCircleElement): string {
		return [
			area.cx.baseVal.value,
			area.cy.baseVal.value,
			area.r.baseVal.value
		].join(',');
	}
}

class SpTeShapeEllipse implements ISpTeShape<SVGEllipseElement> {
	readonly shape = 'ellipse';

	createAction: SpTeSpatialCreateAction = new SpTeSpatialCreateAction(this)
		.setLabel("Ellipse")
		.setDescription("Création d'une ellipse")
		.setIcon("/@skin@/edit/wed/spte/spatial/ellipse.svg");

	tplArea(spShape: string, spCoords: string, spTeSegment: SpTeSegmentWedlet, selected: boolean): TemplateResult {
		const coords = spCoords.split(',');
		return svg`<ellipse class="area" cx="${coords?.[0] || '0'}" cy="${coords?.[1] || '0'}" rx="${coords?.[2] || '0'}" ry="${coords?.[3] || '0'}" p.spTeSegment="${spTeSegment}" b.spte-selected="${selected}" spte-shape="${spShape}" tabindex="0"/>`;
	}

	emptyArea(): SVGEllipseElement {
		return JSX.asSvg(() => <ellipse class="area"/>) as SVGEllipseElement;
	}

	translateArea(area: SVGEllipseElement, x: number, y: number): void {
		area.cx.baseVal.value += x;
		area.cy.baseVal.value += y;
	}

	updateAreaFromNodes(area: SVGEllipseElement, nodes: ISpTeSpatialNode[], nodeIndex: number): void {
		if (nodeIndex == 0) area.rx.baseVal.value = Math.abs(nodes[0].x - area.cx.baseVal.value);
		else area.ry.baseVal.value = Math.abs(nodes[1].y - area.cy.baseVal.value);
	}

	updateAreaFromRect(area: SVGEllipseElement, point1: GFX.IPoint, point2: GFX.IPoint) {
		area.cx.baseVal.value = point1.x;
		area.cy.baseVal.value = point1.y;
		area.rx.baseVal.value = Math.abs(point2.x - point1.x);
		area.ry.baseVal.value = Math.abs(point2.y - point1.y);
	}

	areaNodes(area: SVGEllipseElement): Iterable<ISpTeSpatialNode> {
		return [
			{x: area.cx.baseVal.value + area.rx.baseVal.value, y: area.cy.baseVal.value},
			{x: area.cx.baseVal.value, y: area.cy.baseVal.value + area.ry.baseVal.value}
		];
	}

	areaCoords(area: SVGEllipseElement): string {
		return [
			area.cx.baseVal.value,
			area.cy.baseVal.value,
			area.rx.baseVal.value,
			area.ry.baseVal.value
		].join(',');
	}
}

class SpTeShapePolygon implements ISpTeShape<SVGPolygonElement> {
	readonly shape = 'poly';

	createAction: SpTeSpatialCreateAction = new SpTeSpatialCreateAction(this)
		.setLabel("Polygone")
		.setDescription("Création d'un polygone")
		.setIcon("/@skin@/edit/wed/spte/spatial/poly.svg");

	tplArea(spShape: string, spCoords: string, spTeSegment: SpTeSegmentWedlet, selected: boolean): TemplateResult {
		const coords = spCoords.split(',');
		let points = '';
		if (coords && coords.length > 1) {
			for (let i = 0; i < coords.length; i += 2) {
				points += `${coords[i]},${coords[i + 1]} `;
			}
		} else {
			points = '0,0'
		}
		return svg`<polygon class="area" points="${points}" p.spTeSegment="${spTeSegment}" b.spte-selected="${selected}" spte-shape="${spShape}" tabindex="0"/>`;
	}

	emptyArea(): SVGPolygonElement {
		return JSX.asSvg(() => <polygon class="area"/>) as SVGPolygonElement;
	}

	translateArea(area: SVGPolygonElement, x: number, y: number): void {
		for (let i = 0; i < area.points.numberOfItems; i++) {
			const point = area.points.getItem(i);
			point.x += x;
			point.y += y;
		}
	}

	areaCoords(area: SVGPolygonElement): string {
		const coords = [];
		for (let i = 0; i < area.points.numberOfItems; i++) {
			const point = area.points.getItem(i);
			coords.push(point.x, point.y);
		}
		return coords.join(',');
	}

	areaNodes(area: SVGPolygonElement): Iterable<GFX.IPoint> {
		return area.points as any as Iterable<SVGPoint>;
	}
}

REG.reg.addToList("wed:spTeSpatial:shapes", "rect", 1, new SpTeShapeRect(), 0);
REG.reg.addToList("wed:spTeSpatial:shapes", "roundedRect", 1, new SpTeShapeRect(true), 10);
REG.reg.addToList("wed:spTeSpatial:shapes", "circle", 1, new SpTeShapeCircle(), 20);
REG.reg.addToList("wed:spTeSpatial:shapes", "ellipse", 1, new SpTeShapeEllipse(), 30);
REG.reg.addToList("wed:spTeSpatial:shapes", "poly", 1, new SpTeShapePolygon(), 40);


class RoundedRectNode implements ISpTeSpatialNode {
	protected _y: number;

	constructor(readonly _rect: SVGRectElement, y: number) {
		this.y = y;
	}

	readonly shape = 'diamond';

	get x(): number {
		return this._rect.x.baseVal.value + this._rect.width.baseVal.value;
	}

	set x(val) {
	}

	get y(): number {
		return this._y;
	}

	set y(val: number) {
		const rectY = this._rect.y.baseVal.value;
		const minMaxR = Math.min(this._rect.width.baseVal.value / 2, this._rect.height.baseVal.value / 2);
		this._y = Math.max(rectY, Math.min(rectY + minMaxR, val));
	}
}

function appendHandler(ctx: SpTeWallpaper, node: ISpTeSpatialNode): ISpTeSpatialHandler {
	let handler: SVGRectElement | SVGCircleElement;
	if (node.shape == 'diamond') {
		const halfWidth = Math.sqrt(SPATIAL_HANDLERS_WIDTH * 2) / ctx.zoomLevel;
		handler = document.createElementNS(DOM.SVG_NS, 'rect') as SVGRectElement;
		SVG.assignRectLengths(handler, {
			x: node.x - halfWidth,
			y: node.y - halfWidth,
			width: halfWidth * 2,
			height: halfWidth * 2
		});
		const transform = ctx.canvas.createSVGTransform();
		transform.setRotate(45, node.x, node.y);
		handler.transform.baseVal.initialize(transform);
	} else {
		handler = document.createElementNS(DOM.SVG_NS, 'circle') as SVGCircleElement;
		handler.r.baseVal.value = SPATIAL_HANDLERS_WIDTH / ctx.zoomLevel / 2;
		handler.cx.baseVal.value = node.x;
		handler.cy.baseVal.value = node.y;
	}
	handler.classList.add('handler');
	(handler as ISpTeSpatialHandler).node = node;
	ctx.toolsLayer.appendChild(handler);
	handler.style.strokeWidth = parseFloat(getComputedStyle(handler).strokeWidth) / ctx.zoomLevel + 'px';
	return handler as ISpTeSpatialHandler;
}
